import React, { useContext, useState } from 'react';
import { Context } from '../../context/news_search.context';
import { ExtendedSavedSearch } from '../../types/news_search.types';
import Tooltip from '../../../components/react/tooltip/tooltip';
import styles from './saved_searches.module.scss';
import {
  deleteAlert,
  createAlert,
  editAlert,
} from '../../../services/bookmarked_searches/bookmarked_searches.service';
import ModalAlertStatus from '../modal_alert_status/modal_alert_status';
import ModalDeleteSearch from '../modal_delete_search/modal_delete_search';
import classNames from 'classnames';
import { GENERIC_ERROR_MESSAGE } from '../../../components/react/alert_popup/constants';

const SavedSearches: React.FC = () => {
  const [keywordFilter, setKeywordFilter] = useState<string>('');

  const {
    savedSearches,
    setSavedSearches,
    setSavedSearchId,
    setAppliedSavedSearch,
    setShowStatusModal,
    editedSavedSearch,
    setEditedSavedSearch,
    setShowDeleteSearchModal,
    setSearchToBeDeleted,
    setAlertData,
  } = useContext(Context);

  const onStatusClick = (search: ExtendedSavedSearch): void => {
    const myActiveAlert = search.alerts.find((alert) => alert.isMyAlert);
    // If the search contains an alert that is mine/active, then deactivate it
    if (myActiveAlert) {
      deleteAlert(myActiveAlert.id)
        .then(() => {
          const updatedSavedSearches = savedSearches.map((savedSearch: ExtendedSavedSearch) => {
            return search.id !== savedSearch.id
              ? savedSearch
              : {
                ...search,
                alerts: [],
              };
          });

          setSavedSearches(updatedSavedSearches);
          setAlertData({
            type: 'success',
            message: `You have successfully deactivated '${search.title}'.`,
          });
        })
        .catch(() => {
          setAlertData({
            type: 'error',
            message: `An error occurred while deactivating "${search.title}"`,
          });
        });
    } else {
      setEditedSavedSearch(search);
      setShowStatusModal(true);
    }
  };

  const toggleAlertPeriodicity = (searchToToggle: ExtendedSavedSearch): void => {
    const alertToToggle = searchToToggle.alerts.find((alert) => alert.isMyAlert);
    // If the search does not hold any active alerts assigned to current user,
    // do not toggle any periodicity alert
    if (!alertToToggle) return;
    const { how_often, id } = alertToToggle;

    editAlert(id, how_often === 1 ? 7 : 1)
      .then((modifiedAlert) => {
        const updatedSearches = savedSearches.map((search) => {
          if (searchToToggle.id !== search.id) return search;
          return {
            ...searchToToggle,
            alerts: [
              {
                ...modifiedAlert,
                isMyAlert: true,
                periodicityDisplay: modifiedAlert.how_often === 1 ? 'Daily' : 'Weekly',
              },
            ],
          };
        });
        setSavedSearches(updatedSearches as ExtendedSavedSearch[]);
        setAlertData({
          type: 'success',
          message: `"${searchToToggle.title}" frequency has been successfully modified.`,
        });
      })
      .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, }));
  };

  const onAlertActivationClick = (periodicity: 'Daily' | 'Weekly'): void => {
    createAlert(periodicity === 'Daily' ? 1 : 7, editedSavedSearch.id)
      .then((response) => {
        const updatedSearches = savedSearches.map((savedSearch: ExtendedSavedSearch) => {
          if (savedSearch.id !== editedSavedSearch.id) return savedSearch;

          return {
            ...savedSearch,
            alerts: [
              {
                ...response,
                isMyAlert: true,
                periodicityDisplay: periodicity,
              },
            ],
          };
        });

        setSavedSearches(updatedSearches);
        setAlertData({
          type: 'success',
          message: `You have successfully activated '${editedSavedSearch.title}'.`,
        });
      })
      .catch(() => setAlertData({ type: 'error', message: GENERIC_ERROR_MESSAGE, }))
      .finally(() => {
        setShowStatusModal(false);
        setEditedSavedSearch({});
      });
  };

  // Before triggering the deletion of the saved search, display a modal to confirm users' intention
  const onInitSavedSearchDeletion = (savedSearch: ExtendedSavedSearch): void => {
    setSearchToBeDeleted(savedSearch);
    setShowDeleteSearchModal(true);
  };

  const onFilterChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = event.target;
    setKeywordFilter(value.trim());
  };

  const renderVisibleSavedSearches: JSX.Element[] = savedSearches
    .filter((search) => search.title.toLowerCase().includes(keywordFilter.toLowerCase()))
    .map(
      (search: ExtendedSavedSearch): JSX.Element => (
        <div key={search.id} className={`row py-2 ${styles.alerts}`}>
          <div className='col-6'>
            <span
              className='btn-link cursor-pointer link-blue-orange p-15'
              onClick={(): void => {
                setSavedSearchId(`${search.id}`);
                setAppliedSavedSearch(search);
              }}
            >
              {search.title}
            </span>
          </div>
          <div className='col-2 text-center'>
            <Tooltip
              placement='left'
              message={search.alerts.some((alert) => alert.isMyAlert) ? 'Deactivate' : 'Activate'}
            >
              <span
                className='cursor-pointer btn-link p-15'
                onClick={() => onStatusClick(search)}
              >
                <i
                  className={`fal ${
                    search.alerts.some((alert) => alert.isMyAlert)
                      ? `icon-bell ${styles.activeAlertIcon}`
                      : `icon-bell-slash ${styles.inactiveAlertIcon}`
                  }`}
                />
              </span>
            </Tooltip>
          </div>
          <div className='col-2 text-center'>
            <Tooltip
              placement='left'
              message={
                search.alerts.find((alert) => alert.isMyAlert)?.periodicityDisplay || 'Inactive'
              }
            >
              <span
                className={classNames('btn-link p-15', styles.linkOrangeBlue, {
                  'cursor-pointer': search.alerts.some((alert) => alert.isMyAlert),
                })}
                onClick={() => toggleAlertPeriodicity(search)}
              >
                <i
                  className={classNames(
                    'fal',
                    search.alerts.some((alert) => alert.isMyAlert)
                      ? `${styles.activeAlertIcon} ${
                        search.alerts.find((alert) => alert.isMyAlert)?.periodicityDisplay ===
                          'Daily'
                          ? 'icon-calendar-alt'
                          : 'icon-calendar'
                      }`
                      : `icon-times ${styles.inactiveAlertIcon}`
                  )}
                />
              </span>
            </Tooltip>
          </div>
          <div className='col-2 text-center' onClick={() => onInitSavedSearchDeletion(search)}>
            <span className='cursor-pointer btn-link p-15 link-blue-orange'>
              <i className='fal icon-trash' />
            </span>
          </div>
        </div>
      )
    );

  const renderNoVisibleSavedSearches: JSX.Element = (
    <p data-testid='no-visible-saved-searches' className='my-1 text-gray-li fs-7 p-15'>
      No alerts found
    </p>
  );

  const renderSavedSearchesList: JSX.Element = (
    <>
      <ModalDeleteSearch />
      <ModalAlertStatus onAlertActivationClick={onAlertActivationClick} />

      <div className='row'>
        <div className='col-12 pt-1 pb-4'>
          <input
            type='text'
            onChange={onFilterChange}
            className={`w-100 form-control ${styles.keywordFilterInput}`}
            placeholder='Search in your alerts'
          />
        </div>
      </div>
      <div className={`row d-none d-md-flex pb-2 ${styles.labels}`}>
        <div className='col-6'>Alert name</div>
        <div className='col-2 text-center'>Status</div>
        <div className='col-2 text-center'>Frequency</div>
        <div className='col-2 text-center'>Delete</div>
      </div>
      {savedSearches.some((search) =>
        search.title.toLowerCase().includes(keywordFilter.toLowerCase())
      )
        ? renderVisibleSavedSearches
        : renderNoVisibleSavedSearches}
    </>
  );

  const renderNoSavedSearches: JSX.Element = (
    <p data-testid='no-saved-searches' className='mb-1 text-gray-li'>
      No saved searches found. Make a search and save it first.
    </p>
  );
  return savedSearches.length ? renderSavedSearchesList : renderNoSavedSearches;
};

export default SavedSearches;
